import React, { useState, useEffect } from "react";
import {
  Trash2,
  Plus,
  X,
  Upload,
  Image as ImageIcon,
  Video,
} from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";
import Loader from "../../components/common/Loader";

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [fileType, setFileType] = useState(""); // "image" or "video"
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_UPLOAD_PRESET;
  const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_NAME;
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchTestimonials = async () => {
    try {
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${API_URL}/testimonial/get-all`,
        headers: {},
      };
      const startTime = Date.now();

      const response = await axios.request(config);

      if (response.data) {
        setTestimonials(response?.data?.data);
      }
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(1000 - elapsed, 0);

      setTimeout(() => setIsLoading(false), remaining);
    } catch (err) {
      console.error("Error fetching testimonials:", err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleDelete = (testimonialId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        let config = {
          method: "delete",
          maxBodyLength: Infinity,
          url: `${API_URL}/testimonial/delete/${testimonialId}`,
          headers: {},
        };

        axios
          .request(config)
          .then((response) => {
            console.log("Deleted:", response.data);
            Swal.fire({
              title: "Deleted!",
              text: "The testimonial has been deleted successfully.",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
            fetchTestimonials();
          })
          .catch((error) => {
            console.error("Delete error:", error);
            Swal.fire({
              title: "Error!",
              text: "Something went wrong while deleting the testimonial.",
              icon: "error",
            });
          });
      } else {
        Swal.fire({
          title: "Cancelled",
          text: "Your testimonial is safe 😊",
          icon: "info",
          timer: 1200,
          showConfirmButton: false,
        });
      }
    });
  };

  const handleAddTestimonial = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
    setPreviewUrl("");
    setFileType("");
    setIsUploading(false);
    setUploadProgress(0);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const isValid = isImage || isVideo;

    if (!isValid) {
      Swal.fire({
        title: "Invalid File",
        text: `${file.name} is not an image or video file.`,
        icon: "error",
        timer: 2000,
      });
      return;
    }

    if (isVideo && file.size > 50 * 1024 * 1024) {
      Swal.fire({
        title: "File Too Large",
        text: `${file.name} exceeds 50MB limit for videos.`,
        icon: "error",
        timer: 2000,
      });
      return;
    }

    setSelectedFile(file);
    setFileType(isImage ? "image" : "video");

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setFileType("");
  };

  const uploadToCloudinary = async (file, type) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("cloud_name", CLOUDINARY_CLOUD_NAME);

    const resourceType = type === "video" ? "video" : "image";
    formData.append("resource_type", resourceType);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${resourceType}/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Cloudinary upload failed: ${response.status}`);
      }

      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error("Error uploading to Cloudinary:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      Swal.fire({
        title: "Error!",
        text: "Please select an image or video to upload.",
        icon: "error",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const url = await uploadToCloudinary(selectedFile, fileType);
      setUploadProgress(100);

      let data = JSON.stringify({
        video: url,
      });

      let config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${API_URL}/testimonial/add`,
        headers: {
          "Content-Type": "application/json",
        },
        data: data,
      };

      await axios.request(config);

      Swal.fire({
        title: "Success!",
        text: `Testimonial ${fileType} uploaded successfully.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      handleCloseModal();
      fetchTestimonials();
    } catch (error) {
      console.error("Error uploading testimonial:", error);
      Swal.fire({
        title: "Error!",
        text: "Something went wrong while uploading the testimonial.",
        icon: "error",
      });
      setIsUploading(false);
    }
  };

  const isVideo = (url) => {
    return url.includes("/video/upload/") || url.match(/\.(mp4|webm|mov)$/i);
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="w-full mx-auto">
        <div className="mb-4 mt-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">
                Testimonials
              </h1>
            </div>
            <button
              className="bg-primaryBtnColor text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-300 shadow-md hover:shadow-lg"
              onClick={handleAddTestimonial}
            >
              <Plus size={20} />
              Add Testimonial
            </button>
          </div>
        </div>

        {isLoading ? (
          <Loader />
        ) : error ? (
          <div className="col-span-full text-center py-16">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              Error Loading Testimonials
            </h3>
            <p className="text-gray-600">{error}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial._id}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 group relative overflow-hidden"
              >
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                  <button
                    onClick={() => handleDelete(testimonial._id)}
                    className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-all shadow-md"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="aspect-video overflow-hidden relative bg-gray-100">
                  {isVideo(testimonial.video) ? (
                    <>
                      <video
                        src={testimonial.video}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        controls={false}
                        muted
                        playsInline
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                        <div className="bg-white bg-opacity-95 rounded-full p-3 transform transition-transform duration-300 group-hover:scale-110 shadow-2xl">
                          <Video
                            className="w-8 h-8 text-red-700"
                            fill="currentColor"
                          />
                        </div>
                      </div>
                      <div className="absolute top-2 right-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        VIDEO
                      </div>
                    </>
                  ) : (
                    <>
                      <img
                        src={testimonial.video}
                        alt="Testimonial"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/400x300?text=Media+Not+Found";
                        }}
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                        <p className="text-white text-lg font-medium">
                          Testimonial
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <div className="h-1 bg-gradient-to-r from-blue-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && testimonials.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🎥</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              No Testimonials Found
            </h3>
            <p className="text-gray-600 mb-6">
              Start by adding your first testimonial
            </p>
            <button
              onClick={handleAddTestimonial}
              className="bg-primaryBtnColor text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-all duration-300"
            >
              <Plus size={20} />
              Add Testimonial
            </button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-800">
                Add Testimonial
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                disabled={isUploading}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-800 mb-3">
                    Upload Image or Video *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <ImageIcon size={48} className="text-gray-400 mb-3" />
                      <span className="text-blue-600 font-semibold mb-1">
                        Click to upload
                      </span>
                      <span className="text-sm text-gray-500">
                        or drag and drop image/video here
                      </span>
                      <span className="text-xs text-gray-400 mt-2">
                        Images: PNG, JPG, JPEG, GIF | Videos: MP4, WebM (Max
                        50MB)
                      </span>
                    </label>
                  </div>
                </div>

                {previewUrl && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                      Preview
                    </h3>
                    <div className="relative group">
                      <div className="aspect-video rounded-lg overflow-hidden border border-gray-200 bg-gray-100 relative">
                        {fileType === "video" ? (
                          <>
                            <video
                              src={previewUrl}
                              className="w-full h-full object-cover"
                              controls={false}
                              muted
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                              <Video size={48} className="text-white" />
                            </div>
                          </>
                        ) : (
                          <img
                            src={previewUrl}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      {!isUploading && (
                        <button
                          type="button"
                          onClick={handleRemoveFile}
                          className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      )}
                      <p className="text-xs text-gray-600 mt-2">
                        {selectedFile?.name} ({fileType})
                      </p>
                    </div>
                  </div>
                )}

                {isUploading && (
                  <div className="mt-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-semibold text-gray-800">
                        Uploading...
                      </span>
                      <span className="text-sm text-gray-600">
                        {uploadProgress}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-all"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUploading || !selectedFile}
                  className="bg-primaryBtnColor text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Add Testimonial
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
